import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  DuBackendRoleCreatePayload,
  DuBackendRoleUpdatePayload,
} from "../models/du-admin-backend-role.model";
import { ApiService } from "src/app/services/api.service";
import { SessionService } from "src/app/services/session.service";

/** Backend calls for the DU Admin module (UserManagementController on the API
 *  side — the controller formerly known as Admin, hence the route below). */
@Injectable({
  providedIn: "root",
})
export class DuAdminService {
  constructor(
    private apiService: ApiService,
    private httpClient: HttpClient,
    private sessionService: SessionService
  ) {}

  /**
   * Admin POSTs go through HttpClient rather than `ApiService.post`, which
   * merges the session's `client_key` / `employee_key` / `module_key` over the
   * body. That would silently retarget these calls — most damagingly
   * update-user, where the session's employee_key would replace the one being
   * edited. `client_key` is stamped deliberately by `scoped` instead.
   */
  private post(path: string, body: any): Observable<any> {
    return this.httpClient.post(`${this.apiService.apiBaseUrl}/${path}`, body);
  }

  /**
   * POST whose reply is read as text.
   *
   * `MyInterceptor` returns early for any request whose `responseType` is not
   * "json", so it never attaches the Cognito token — a text-response call would
   * reach the API unauthenticated and be rejected. Attach the same headers the
   * interceptor would have added instead.
   */
  private postText(path: string, body: any): Observable<any> {
    return from(fetchAuthSession()).pipe(
      switchMap((session) => {
        const token = session.tokens?.idToken?.toString() ?? "";
        const headers: { [header: string]: string } = {
          Authorization: `Bearer ${token}`,
          user_id: this.sessionService.getUsername() ?? "",
          "Content-Type": "application/json",
        };

        // Mirrors the interceptor: only sent when the current page has schema checks.
        const schemaType = sessionStorage.getItem("schema_type");
        if (schemaType) {
          headers["schema_type"] = schemaType;
        }

        return this.httpClient.post(
          `${this.apiService.apiBaseUrl}/${path}`,
          body,
          { headers, responseType: "text" }
        );
      })
    );
  }

  /**
   * The client every scoped admin call is stamped with.
   *
   * The module has no client picker of its own: the client chosen at sign-in is
   * the one being administered, and it is read from sessionStorage. Reading it
   * in one place is what keeps the role lists and `update-user` in step — a role
   * key fetched for one client is silently dropped when saved against another.
   */
  getClientKey(): string {
    const key = this.apiService.getClientKey();
    return key === false || key == null ? "" : String(key).trim();
  }

  /** The signed-in client's name, for screen copy. Blank when unknown. */
  getClientName(): string {
    const client = this.sessionService.getClient();
    return String(client?.client_nm ?? client?.CLIENT_NM ?? "").trim();
  }

  /** Body plus the session's `client_key`. Every endpoint that reads or writes
   *  client-owned rows goes through here, so none of them can be sent unscoped. */
  private scoped(body: any): any {
    return { ...(body || {}), client_key: this.getClientKey() };
  }

  /** Every column of every row of the user master, so the caller must not
   *  assume a fixed set of keys. `[]` for an empty table.
   *
   *  A GET, and deliberately *not* client-scoped: the endpoint takes no body and
   *  no parameters, and answers with the whole table (minus `password`) rather
   *  than one client's members. That matches what the user screens are — pick
   *  any user, then configure this client's access for them — so the directory
   *  is the master list and `update-user` is what makes a row this client's.
   *
   *  The two role lists are NOT like this: they stay client-scoped POSTs, and
   *  still filter by client. */
  getUserList(): Observable<any> {
    return this.apiService.get("api/usermanagement/get-user-list");
  }

  /** Every client: `{ client_key, client_nm }` rows. Unlike `api/get-client-list`
   *  this is not scoped to the signed-in user. Kept for lookups only — the admin
   *  screens administer the session's client and offer no client choice. */
  getClientList(): Observable<any> {
    let url = `api/usermanagement/get-client-list`;
    return this.apiService.get(url);
  }

  /** Every frontend role the screen may offer, and what the role directory
   *  lists: `{ user_role_key, user_role_nm, client_key }` rows for the signed-in
   *  client **and** for Global (9999), in one response, client rows first.
   *
   *  On the user screens the two are tracked and submitted separately, so the
   *  caller splits them on `client_key` — see `readRoleOptions`. On the role
   *  directory the split decides which rows may be edited: only the session
   *  client's. */
  getRoleList(): Observable<any> {
    return this.post("api/usermanagement/get-role-list", this.scoped({}));
  }

  /** As `getRoleList`, for the backend (data filter) roles — keyed on
   *  `user_role_filter_config_key` rather than `user_role_key`. Global rows come
   *  back here too, and are split off the same way. */
  getBackendRoleList(): Observable<any> {
    return this.post(
      "api/usermanagement/get-backend-role-list",
      this.scoped({})
    );
  }

  /** Responds `{ user_exist, user_linked_to_client, message }` — see
   *  `DuUserExistsCheck`. Two independent flags:
   *
   *  - `user_exist` — the id is in the user master, under ANY client.
   *  - `user_linked_to_client` — that user is already configured for the
   *    `client_key` sent. The one case the create screen refuses; `user_exist`
   *    without it is a user this client may be added to.
   *  - `message` — the API's own wording for `user_linked_to_client` ("Client
   *    configuration is / is not done for this client"), shown as it comes.
   *
   *  Client-scoped, like `get-user-details`, and for the same reason: `user_id`
   *  is unique across the whole user table, but what the screen has to know is
   *  whether it is taken *here*. Unscoped — or scoped to Global (9999), which
   *  owns no users — nothing can be linked, so the second flag is false and the
   *  message falls back to plain existence wording. */
  checkUserExists(userId: string): Observable<any> {
    return this.post(
      "api/usermanagement/check-user-exists",
      this.scoped({ user_id: userId })
    );
  }

  /** One user, as `{ user, access, global_access }`:
   *
   *  - `user` — the user master row minus `password`, as an array of 0 or 1,
   *    with lowercase column names.
   *  - `access` — exactly one entry, for the client asked for, carrying
   *    `user_role_keys` and `user_role_filter_config_keys`. Always present, with
   *    empty arrays when the user holds nothing here.
   *  - `global_access` — the same shape for client 9999. Always present too.
   *
   *  - `territory_keys` — the user's territories for the `brand_key` sent, as
   *    `territory_dim_key` strings. Always present, `[]` when none.
   *
   *  A user id that does not resolve under this client answers with an empty
   *  `user` array, which is an empty state rather than an error. */
  getUserDetails(userId: string, brandKey = ""): Observable<any> {
    const body: any = { user_id: userId };
    // No brand picked yet: nothing to scope territories to, so none is sent
    // rather than a blank one.
    if (brandKey) body.brand_key = brandKey;
    return this.post("api/usermanagement/get-user-details", this.scoped(body));
  }

  /** The territories one brand offers, as `{ territory_dim_key, territory_name }`
   *  rows sorted by name. Territories live in the brand's own config schema, so
   *  the list is a function of the brand and is refetched whenever it changes.
   *  `[]` is a brand with none. */
  getTerritoryList(brandKey: string): Observable<any> {
    return this.post("api/usermanagement/get-territory-list", {
      brand_key: brandKey,
    });
  }

  /** Creates or updates, keyed on `employee_key`: blank inserts a new user, a
   *  value updates that employee. `password` is generated server-side on create
   *  and must never be posted; `usr_email_id` is written from `user_id`.
   *
   *  `client_key` is required, and stamping it here is what stops it disagreeing
   *  with the client the role lists were fetched for. Only that client's access
   *  is rewritten — every other client the user belongs to is left alone — and
   *  Global roles are rewritten only when `global_access` is sent, so the caller
   *  sends it on every save even when nothing is selected.
   *
   *  `territory_keys` works the same way for the `brand_key` sent: the list
   *  replaces the user's territories for that brand, `[]` clears them, and
   *  leaving it out leaves them as stored. On create the API writes them
   *  against the new employee key itself.
   *
   *  Read as text, not JSON: business failures answer HTTP 200 with the reason
   *  as a bare string ("Success", "User allready exists", "Role 21 does not
   *  exist"), which the default JSON response type would fail to parse. Genuine
   *  faults are HTTP 500 with the message as the body. */
  saveUser(user: any): Observable<any> {
    return this.postText("api/usermanagement/update-user", this.scoped(user));
  }

  /** The catalogue one client can grant from, as
   *  `{ modules: [ { module_key, module_nm, dashboards: [ { …, pages: […] } ] } ] }`
   *  — the modules that client is licensed for through
   *  `du_ana_module_client_xref`, and the dashboards and pages under them.
   *
   *  A GET with `client_key` on the query string, like `getClientList` but
   *  scoped. The parameter is *optional* server-side and omitting it answers with
   *  the whole master tree at HTTP 200 rather than an error, so a missing client
   *  would silently over-grant instead of failing. It is required here for
   *  exactly that reason, and a blank one fails loudly rather than being sent as
   *  nothing — callers pass the client the screen is scoped to (create) or the
   *  role’s own `client_key` (edit and view), never a default.
   *
   *  A module with no dashboards still arrives, with `dashboards: []`, and so
   *  does a dashboard with no pages. `modules: []` is a client licensed for
   *  nothing, which is an empty state rather than a failure. */
  getPermissionTree(clientKey: string): Observable<any> {
    const key = String(clientKey ?? "").trim();
    if (!key) {
      return throwError(
        () =>
          new Error(
            "client_key is required to load the permission tree: without it the API answers with every module in the master."
          )
      );
    }

    return this.apiService.get(
      `api/usermanagement/get-permission-tree?client_key=${encodeURIComponent(
        key
      )}`
    );
  }

  /** One frontend role and its access, as `{ role, modules, dashboards, pages }`:
   *
   *  - `role` — the `du_ana_user_role_master` row as a serialised DataTable, so
   *    an *array* of 0 or 1. The caller reads `role[0]`.
   *  - `modules` / `dashboards` / `pages` — three **flat** lists, and unlike the
   *    save these are *complete*: every config row the role holds. `modules` is
   *    therefore the membership on the way in, which is where `module_keys` is
   *    derived from on the way out. Dashboard rows carry `module_key` and page rows
   *    `dashboard_key`, so the hierarchy is recoverable without a second lookup.
   *
   *  Each flag comes back exactly as the column holds it, `null` included — which
   *  is a real state and not "N". See rule 3 in `du-admin-permission.model.ts`.
   *
   *  A key the API does not know answers all four keys empty at HTTP 200, which is
   *  an empty state rather than an error.
   *
   *  Deliberately *not* client-scoped: any key resolves, whoever owns it. The
   *  caller has to check `role[0].client_key` against the session's client
   *  before offering to edit — see `isForeignRole`. */
  getRoleDetails(userRoleKey: string): Observable<any> {
    return this.post("api/usermanagement/get-role-details", {
      user_role_key: userRoleKey,
    });
  }

  /** Creates one frontend role from
   *  `{ user_role_nm, client_key, module_keys, modules, dashboards, pages }` and
   *  answers `{ success, message, user_role_key }`.
   *
   *  `module_keys` is the role's membership — the API derives every dashboard and
   *  page row from it. The other three lists only carry flags that differ from the
   *  insert defaults, so on a new role they are usually all `[]`.
   *
   *  `client_key` is stamped here, so a role cannot be created against a client
   *  other than the one being administered.
   *
   *  JSON, not text: unlike the older endpoints in this controller this one
   *  answers with an object. A validation failure is still HTTP 200 — with
   *  `success: false` and the reason in `message` — so the caller branches on
   *  `success` and never on the status code. A genuine fault is HTTP 500 with a
   *  bare string body, as elsewhere in the controller. */
  createRole(role: any): Observable<any> {
    return this.post("api/usermanagement/create-role", this.scoped(role));
  }

  /** Updates one frontend role. Same body as `createRole` plus `user_role_key`,
   *  and the same `{ success, message }` reply on the same HTTP 200.
   *
   *  The three lists are not the same kind of thing, and mixing them up is the
   *  easiest way to corrupt a role:
   *
   *  - `module_keys` is the role's *whole* membership and is reconciled against. A
   *    module left out has its module row, its dashboard rows and its page rows
   *    deleted; `[]` strips the role to nothing while keeping its name. Never a
   *    delta, and never omitted — sending `modules` without it is refused rather
   *    than obeyed.
   *  - `modules`, `dashboards` and `pages` add and remove nothing — those rows are
   *    derived from `module_keys` either way. They are sparse *diffs*: an entry
   *    only where a flag moved, and inside it only the flags that moved. An
   *    omitted entry means "leave that column alone", so an untouched role sends
   *    all three as `[]`. Every entry must name a module `module_keys` covers.
   *
   *  `client_key` is not stamped. Omitting it leaves the role on the client it
   *  was created under, which is what the edit screen wants — pass
   *  `moveToSessionClient` only to deliberately move a role between clients.
   *
   *  There is no delete endpoint for a role, here or anywhere, so no screen
   *  offers one. A *backend* (data filter) role has its own quartet of endpoints
   *  and none of this applies to it — see `createBackendRole` and
   *  `updateBackendRole`, whose grant lists replace rather than diff. */
  updateRole(role: any, moveToSessionClient = false): Observable<any> {
    return this.post(
      "api/usermanagement/update-role",
      moveToSessionClient ? this.scoped(role) : role
    );
  }

  /** The brands one client holds, as `{ brand_dim_key, brand_name }` rows.
   *
   *  Step 2 of the backend role flow. `client_key` is a top-level field of the
   *  JSON body, not a query param and not nested.
   *
   *  Required, and blank fails loudly, for the same reason as
   *  `getPermissionTree`: the API answers `[]` for a missing `client_key` rather
   *  than refusing it, and `[]` is indistinguishable from a client that holds no
   *  brands — so the mistake would read as an empty state instead of a bug. */
  getBrandList(clientKey: string): Observable<any> {
    const key = String(clientKey ?? "").trim();
    if (!key) {
      return throwError(
        () =>
          new Error(
            "client_key is required to load the brand list: without it the API answers with an empty list, which reads as a client holding no brands."
          )
      );
    }

    return this.post("api/usermanagement/get-brand-list", { client_key: key });
  }

  /** The sources linked to the given brands, as
   *  `{ data_source_dim_key, data_source_name }` rows — a deduped union across
   *  every key sent, already scoped by the API.
   *
   *  Step 3 of the backend role flow, and it must be re-called on every change to
   *  the brand selection: the list is a function of the keys sent, not a catalogue
   *  to be filtered afterwards.
   *
   *  An empty `brand_dim_keys` answers `[]` — *not* every source, and not "no
   *  filter applied". Callers must not read that as a catalogue, which is why no
   *  brands selected is a state the screen renders rather than a call it makes;
   *  an empty list here fails loudly instead. */
  getSourceList(brandDimKeys: string[]): Observable<any> {
    const keys = (brandDimKeys || [])
      .map((key) => String(key ?? "").trim())
      .filter((key) => !!key);

    if (!keys.length) {
      return throwError(
        () =>
          new Error(
            "brand_dim_keys is required to load the source list: an empty list answers with no sources, which is not the same as no filter."
          )
      );
    }

    return this.post("api/usermanagement/get-source-list", {
      brand_dim_keys: keys,
    });
  }

  /** One backend (data filter) role and its grants, as
   *  `{ role, brands, sources }`:
   *
   *  - `role` — the `du_ana_user_role_filter_config` row as a serialised
   *    DataTable, so an *array* of 0 or 1. The caller reads `role[0]`, which
   *    carries `user_role_filter_config_key`, `user_role_nm` and `client_key`.
   *  - `brands` / `sources` — the grants the role holds, with their names. A grant
   *    whose dimension row has since been deleted still comes back, with its key
   *    and a null name, so the screen can show it rather than drop it.
   *
   *  Keyed on `user_role_filter_config_key`, not `user_role_key`: this is the
   *  filter config table, and it is the same key `getBackendRoleList` returns and
   *  the user screens' backend-role picker holds.
   *
   *  A key the API does not know answers all three arrays empty at HTTP 200, which
   *  is a not-found state rather than a failure.
   *
   *  Not client-scoped: any key resolves, as with `getRoleDetails`. The caller
   *  reads the client off the row rather than assuming the session's. */
  getBackendRoleDetails(userRoleFilterConfigKey: string): Observable<any> {
    return this.post("api/usermanagement/get-backend-role-details", {
      user_role_filter_config_key: userRoleFilterConfigKey,
    });
  }

  /** Creates one backend (data filter) role and answers
   *  `{ success, message, user_role_key }`.
   *
   *  `client_key` is **not** stamped here, unlike `createRole`. These screens
   *  choose their client — an edit resolves it from the role's own brands — so
   *  merging the session's over it is exactly the retargeting `post` exists to
   *  avoid. It is required by the payload type instead, so it cannot be forgotten.
   *
   *  Both key lists go up exactly as they were received — strings, already
   *  numerically normalised by the API. Nothing here pads, casts or reformats
   *  them.
   *
   *  A validation failure is HTTP 200 with `success: false` and the reason in
   *  `message`, which names the specific brand or source that was refused, so the
   *  caller branches on `success` and shows `message` verbatim. A genuine fault is
   *  HTTP 500 with a bare string body, as elsewhere in this controller.
   *
   *  The new role's key comes back as `user_role_filter_config_key` — see
   *  `readBackendRoleSaveResult`, which is why the frontend save reader cannot be
   *  reused here. Role names are unique per client, so the same name may exist
   *  under another one. */
  createBackendRole(role: DuBackendRoleCreatePayload): Observable<any> {
    return this.post("api/usermanagement/create-backend-role", role);
  }

  /** Updates one backend (data filter) role. Same body as `createBackendRole`
   *  plus `user_role_filter_config_key`, and the same `{ success, message }` reply
   *  on the same HTTP 200.
   *
   *  **The two key lists are replacements, not deltas.** There is no per-item
   *  access flag here as there is on a frontend role: `brand_dim_keys` and
   *  `data_source_dim_keys` are the role's grants after the save, and a key left
   *  out is deleted. So every update carries the whole current selection — see
   *  `buildBackendRolePayload`, which is the only thing that builds it.
   *
   *  `client_key` is optional to the API here — omitting it leaves the role where
   *  it is — and a *different* one **moves** the role, which then requires every
   *  brand in the payload to belong to that new client. The screen always sends
   *  the client its grants were picked from, so the two cannot disagree: the
   *  loaded client when nothing moved, the chosen one when the admin moved it
   *  deliberately. Not stamped from the session, for the same reason as on
   *  create. */
  updateBackendRole(role: DuBackendRoleUpdatePayload): Observable<any> {
    return this.post("api/usermanagement/update-backend-role", role);
  }

  /** Ends a user's access by stamping the activation end date on the user
   *  record. Takes the id alone — it is not client-scoped, and the date it
   *  writes is not per-client either, so a user belonging to several clients
   *  loses access to all of them. Screen copy must not call this "remove from
   *  this client". Answers "Success" or "User not found".
   *
   *  Read as text for the same reason as `saveUser`. */
  deactivateUser(userId: string): Observable<any> {
    return this.postText("api/usermanagement/deactive-user", {
      user_id: userId,
    });
  }
}
